// Angular
import { Injectable } from '@angular/core';

export enum StorageType {
  SESSION = 'sessionStorage',
  LOCAL = 'localStorage',
  MEMORY = 'memory'
}

@Injectable()
export class StorageService {
  private memoryStorage: Map<string, string> = new Map();
  private readonly defaultStorageType: StorageType = StorageType.SESSION;

  getItem(key: string, storageType: StorageType = this.defaultStorageType): string | null {
    try {
      switch (storageType) {
        case StorageType.SESSION:
          return typeof window !== 'undefined' && window.sessionStorage
            ? window.sessionStorage.getItem(key)
            : null;
        
        case StorageType.LOCAL:
          return typeof window !== 'undefined' && window.localStorage
            ? window.localStorage.getItem(key)
            : null;
        
        case StorageType.MEMORY:
          return this.memoryStorage.get(key) || null;
        
        default:
          return null;
      }
    } catch (error) {
      console.error(`Error getting item from ${storageType}:`, error);
      return null;
    }
  }

  setItem(key: string, value: string, storageType: StorageType = this.defaultStorageType): void {
    try {
      switch (storageType) {
        case StorageType.SESSION:
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(key, value);
          }
          break;
        
        case StorageType.LOCAL:
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(key, value);
          }
          break;
        
        case StorageType.MEMORY:
          this.memoryStorage.set(key, value);
          break;
      }
    } catch (error) {
      console.error(`Error setting item in ${storageType}:`, error);
    }
  }

  removeItem(key: string, storageType: StorageType = this.defaultStorageType): void {
    try {
      switch (storageType) {
        case StorageType.SESSION:
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(key);
          }
          break;
        
        case StorageType.LOCAL:
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.removeItem(key);
          }
          break;
        
        case StorageType.MEMORY:
          this.memoryStorage.delete(key);
          break;
      }
    } catch (error) {
      console.error(`Error removing item from ${storageType}:`, error);
    }
  }

  clear(storageType: StorageType = this.defaultStorageType): void {
    try {
      switch (storageType) {
        case StorageType.SESSION:
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.clear();
          }
          break;
        
        case StorageType.LOCAL:
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.clear();
          }
          break;
        
        case StorageType.MEMORY:
          this.memoryStorage.clear();
          break;
      }
    } catch (error) {
      console.error(`Error clearing ${storageType}:`, error);
    }
  }

  isAvailable(storageType: StorageType = this.defaultStorageType): boolean {
    try {
      switch (storageType) {
        case StorageType.SESSION:
          return typeof window !== 'undefined' && !!window.sessionStorage;
        case StorageType.LOCAL:
          return typeof window !== 'undefined' && !!window.localStorage;
        case StorageType.MEMORY:
          return true;
        default:
          return false;
      }
    } catch {
      return false;
    }
  }
}

